/**
 * The bridge between a LAYER_STACK result and the station-volumes dock.
 *
 * `StationVolumeDock` is EXTENDED, not forked (Task 11's design rule): layer
 * thickness is N−1 instances of what that dock already renders, on one grid
 * and one alignment. A third ~1400-line dock would triple-maintain the
 * clip-to-station take-over already duplicated between the two existing docks.
 *
 * What the dock needs is a `StationVolumesLayerMeta` and a
 * `StationVolumesJson`. A stack produces neither: it produces ONE
 * `stack.json` with a bin array per LAYER, and a scalar echo of the station
 * parameters on layer metadata. This module turns the pair into the two
 * shapes the dock reads, for whichever layer of the stack is selected.
 *
 * ## What is synthesised, and what is honest about being synthesised
 *
 * - **Bins** are real: `stack.layers[i].stations`, straight from the worker.
 * - **Params** are real, from the scalar echo plus the grid the run actually
 *   used — with one correction the echo cannot make itself: `offsetBinM` is
 *   the EFFECTIVE offset step the section writer clamped to, not the one the
 *   job asked for (`stackOffsetStepM`).
 * - **The alignment block** carries only what the echo has. `equations` is
 *   empty and `staStart` is 0, so display stations equal internal ones for a
 *   stack — the dock still resolves the LIVE alignment out of the registry by
 *   `key` for everything geometric (the walk, the 3D bands, the clip slab),
 *   and the stale-hash chip still compares `sourceHash`.
 * - **The summary** is recomputed from the bins rather than invented: the
 *   worker writes no per-layer summary, and the dock's header would otherwise
 *   have to print figures nobody computed.
 * - **The crossfall check** is real, and it is carried rather than
 *   recomputed. `stack_analysis.py` writes the roll-up as
 *   `layers[i].crossfall` and copies each station LINE's block down onto its
 *   BIN; this module puts the roll-up back on the summary (the header chip)
 *   and the blocks back on the station lines (the chart pane, the table's
 *   cells, the section labels). The one thing it has to RECOVER is the
 *   BANDS — see `stackCrossfallParams`.
 */
import type { LayerStackLayerMeta, StackStationParams } from '@/lib/layerStack/layerMeta';
import type { StackLayerChip } from '@/components/LayerComplianceStrip';
import type {
  CrossfallBand,
  CrossfallParams,
  CrossfallSummary,
  StackLayer,
  StackResult,
  StationBin,
  StationSectionLine,
  StationSummary,
  StationVolumesJson,
  StationVolumesLayerMeta,
  StackVerdict,
} from '@/lib/stationVolumes/types';
import { coverageWeight } from '@/lib/stationVolumes/aggregate';

/**
 * The chips, from the layer's METADATA when the stack document has not been
 * fetched yet and from the document once it has.
 *
 * Both sources agree — postprocess copies the verdicts off the same document
 * — so the strip is on screen the instant the dock opens rather than after a
 * multi-megabyte fetch. Preferring the document keeps a re-fetched, corrected
 * run from being described by a stale metadata copy.
 */
export const stackLayerChips = (meta: LayerStackLayerMeta | null, doc: StackResult | null): StackLayerChip[] => {
  const source: { i: number; name: string; verdict: StackLayerChip['verdict'] }[] = doc?.layers ?? meta?.layers ?? [];
  return source.map((l, idx) => ({ i: typeof l.i === 'number' ? l.i : idx, name: l.name, verdict: l.verdict }));
};

/**
 * The two section planes that bound one layer.
 *
 * `stack_analysis.py::_plane_names` writes `asbuilt:<level>` for every level
 * in level order, THEN `design:<level>` for every level — so layer `i` is
 * bounded by plane `i` (its upper level's as-built) and plane `i + 1`. The
 * decoder names `zTop`/`zBottom` after planes 0 and 1, which is right for
 * layer 0 and wrong for every other one; the section chart takes this pair
 * and re-points those two accessors.
 *
 * `null` when the run wrote no sections, or when the plane list is too short
 * to name the pair — a run written before the stack existed, for instance.
 */
export const stackPlanePair = (meta: LayerStackLayerMeta | null, layerIndex: number): [string, string] | null => {
  const planes = meta?.sections?.planes;
  if (!Array.isArray(planes)) return null;
  const top = planes[layerIndex];
  const bottom = planes[layerIndex + 1];
  return typeof top === 'string' && typeof bottom === 'string' ? [top, bottom] : null;
};

/**
 * The criteria behind the ROLL-UP, for the openable chip.
 *
 * The roll-up is the worst severity in the stack, so the layer that settles
 * the argument is the first one whose own verdict IS that severity — its
 * thickness criteria say which check failed, by how much, and against what.
 * Empty until the stack document is fetched (layer metadata carries verdicts
 * only) and empty for a `PENDING` roll-up, which has nothing to defend.
 */
export const rollUpCriteria = (doc: StackResult | null, rollUp: StackVerdict | null) => {
  if (!doc || !rollUp || rollUp === 'PENDING') return [];
  return doc.layers?.find((l) => l.verdict === rollUp)?.thickness?.criteria ?? [];
};

/** `StationSummary` over a layer's bins. Every figure is a sum or a
 *  cell-weighted mean of what the worker measured — nothing is invented and
 *  nothing that was not measured is reported as a zero.
 *
 *  `crossfall` is the ONE figure that is not recomputed here: the worker
 *  already rolled the check up over every station LINE (including the closing
 *  one, which has no bin), so re-deriving it from the bins would report a
 *  different `judged` count than the CSV and the PDF do. Spread in only when
 *  the layer carried a check, so a run without one produces the same object,
 *  key for key, that it always did. */
export const stackSummary = (bins: StationBin[], crossfall?: CrossfallSummary | null): StationSummary => {
  let cut = 0;
  let fill = 0;
  let area = 0;
  let cells = 0;
  let binCells = 0;
  let low = 0;
  let cumulative = 0;
  let cumMin = 0;
  let cumMax = 0;
  for (const b of bins) {
    cut += b.cutVolumeM3;
    fill += b.fillVolumeM3;
    area += b.cutAreaM2 + b.fillAreaM2;
    cells += b.cellCount;
    binCells += coverageWeight(b);
    if (b.lowCoverage) low += 1;
    cumulative += b.netVolumeM3;
    cumMin = Math.min(cumMin, cumulative);
    cumMax = Math.max(cumMax, cumulative);
  }
  return {
    binCount: bins.length,
    cutVolumeM3: cut,
    fillVolumeM3: fill,
    netVolumeM3: fill - cut,
    // The equal-area figures are a deviation-run quantity; a stack does not
    // produce them and reporting a 0 would be a measurement that was never
    // made. The dock's AEA column is opt-in and reads the BINS, whose own
    // `cutVolumeAeaM3` is `null` here, so the em dash survives.
    cutVolumeAeaM3: 0,
    fillVolumeAeaM3: 0,
    corridorAreaM2: area,
    // Cell-weighted, like every other coverage figure in this feature — never
    // a plain mean over bins (ruling A5).
    meanCoveragePct: binCells > 0 ? (cells / binCells) * 100 : 0,
    lowCoverageBinCount: low,
    cumulativeMinM3: cumMin,
    cumulativeMaxM3: cumMax,
    // Recomputed by the chart from the bins; never read from here.
    balancePointsInternalM: [],
    outsideSwathCellCount: 0,
    outsideStationRangeCellCount: 0,
    ...(crossfall ? { crossfall } : {}),
  };
};

/**
 * The station LINES the section byproduct was cut at.
 *
 * `stack_analysis.py::_write_sections` builds them as
 * `staFrom + k * interval`, with the last edge clamped to `staTo`, and writes
 * one plane row per edge. It records no per-line end AREAS — those come from
 * `compute_station_bins`, which a stack runs per LAYER over the bins, not
 * over the section planes — so every line is `valid: false` and the section's
 * area readout prints an em dash rather than a fabricated `0.00 m²`. The
 * GEOMETRY on screen is real: it is read straight out of the planes.
 */
export const stackStationLines = (
  meta: LayerStackLayerMeta,
  p: StackStationParams,
  bins: StationBin[] = [],
): StationSectionLine[] => {
  const n = meta.sections?.nStationLines ?? 0;
  const from = p.stationFromInternalM ?? 0;
  const to = p.stationToInternalM ?? 0;
  const lines: StationSectionLine[] = [];
  for (let k = 0; k < n; k++) {
    const sta = Math.min(from + k * p.stationIntervalM, to);
    // The CROSSFALL block travels back the way `stack_analysis.py` sent it:
    // it belongs to station LINE `k`, and the worker copied it onto bin `k`
    // (the bin that STARTS there) because the stack CSV never sees the lines.
    // Bin `i` ↔ line `i` is the same pairing `crossfallFor` and `endAreaFor`
    // use, so the table, the chart and the section all quote one section.
    // The LAST line closes the last bin and has no bin of its own, so it
    // stays bare — the asymmetry `StationBin.crossfall` documents, not a
    // gap to fill in.
    const crossfall = bins[k]?.crossfall;
    lines.push({
      k,
      staInternalM: sta,
      // A stack carries no station equations, so display equals internal.
      staDisplayM: sta,
      cutAreaM2: 0,
      fillAreaM2: 0,
      coveragePct: 0,
      valid: false,
      ...(crossfall ? { crossfall } : {}),
    });
  }
  return lines;
};

/**
 * The crossfall BANDS and tolerance for the synthesised `params`: the
 * document's own echo where there is one, reconstructed from the fits where
 * there is not.
 *
 * The dock's table gates its two crossfall columns on `params.crossfall` and
 * explains its em dashes from `params.crossfall[side]` ("this side was never
 * configured" vs "the fit could not resolve it"), so without this a stack
 * gets no columns at all.
 *
 * **The echo** is `stack.json`'s `params.crossfall`, written by
 * `stack_analysis.py::analyse_stack` at the same address `station_bins.py`
 * uses on a single-pair run. It is the run's VALIDATED spec — what was asked
 * for — and it is preferred whenever present: a fit clipped by the grid, or a
 * band that resolved on no station at all, must not silently redraw the band
 * the check was configured with.
 *
 * **The reconstruction** is for documents written before that echo existed.
 * The bands reach the worker in `station_input.json`, on the S3 side of the
 * ECS payload boundary (`preprocess.ts` keeps them out of the scalar echo,
 * capped at 8192 characters), and `layers[i].crossfall` is a SUMMARY with no
 * bands in it — so the only trace left in an old document is the fit itself.
 * `crossfall.py::fit_band` evaluates the fitted line at the band's own
 * `lo`/`hi`, so `asbuiltLine.u0` and `.u1` ARE `fromM` and `toM`, unsigned, on
 * that side; the design fit is the second choice, since an UNCERTAIN side can
 * lose its as-built figures and keep a perfectly good design line. It is exact
 * whenever some station fitted and IMPOSSIBLE when every station failed
 * coverage — the hole the echo was added to close.
 *
 * `null` — no params block at all — when the layer carried no check (the
 * disclosure is per LAYER even though the echo is per RUN), and, on a
 * pre-echo document, when no station could name a band. A
 * `{ left: null, right: null }` block would tell the table both sides were
 * never configured, which is a different and false statement; the header chip
 * still reports the roll-up in that case, because the counts are real whether
 * or not a band can be named.
 */
export const stackCrossfallParams = (
  crossfall: CrossfallSummary | null | undefined,
  bins: StationBin[],
  echoed?: CrossfallParams | null,
): CrossfallParams | null => {
  if (!crossfall) return null;
  // Defended like any other document field: a hand-edited or truncated echo
  // must not put a NaN band on the drawing. An echo that names NEITHER side
  // says nothing the fits cannot say better, so it falls through.
  const named = (b: CrossfallBand | null | undefined): CrossfallBand | null =>
    b && Number.isFinite(b.fromM) && Number.isFinite(b.toM) ? { fromM: b.fromM, toM: b.toM } : null;
  if (echoed && Number.isFinite(echoed.tolerancePct)) {
    const left = named(echoed.left);
    const right = named(echoed.right);
    if (left || right) return { left, right, tolerancePct: echoed.tolerancePct };
  }
  const band = (side: 'left' | 'right'): CrossfallBand | null => {
    for (const b of bins) {
      const fit = b.crossfall?.[side]?.asbuiltLine ?? b.crossfall?.[side]?.designLine;
      if (fit && Number.isFinite(fit.u0) && Number.isFinite(fit.u1)) return { fromM: fit.u0, toM: fit.u1 };
    }
    return null;
  };
  const left = band('left');
  const right = band('right');
  return left || right ? { left, right, tolerancePct: crossfall.tolerancePct } : null;
};

/**
 * The offset column width the run's writers ACTUALLY used — which is not the
 * one that was requested.
 *
 * `stack_analysis.py::_write_sections` clamps to `max(offsetBin, eff_res)`
 * before it cuts a single plane ("a finer offset bin than the grid is
 * fictitious detail") and records the CLAMPED figure as the section block's
 * `offsetStepM`. `station_bins.py` applies the same clamp and writes the
 * clamped figure into `params.offsetBinM` (line 834), so on a real
 * `station_volumes.json` `params.offsetBinM` already IS the effective step.
 *
 * A stack's `stationParams` is the other thing entirely: the scalar echo of
 * the JOB INPUT (`preprocess.ts::stationVolumeWorkerParams`), i.e. the number
 * the user asked for. Synthesising `params.offsetBinM` from that echo hands
 * every consumer of the offset axis — `useStationSections`'s decode, the
 * section chart's fallback axis, the DXF sheet and model exports — a column
 * width the file was never written at. Whenever the grid coarsens (the normal
 * case on a large site, and the case the resolution warning calls out) the
 * dock draws its cross-section at one width while the per-station PDF, which
 * reads the recorded `offsetStepM` server-side, draws it at another. Same
 * bytes, two different corridors, and neither says anything is wrong.
 *
 * So: prefer what the writer RECORDED, and reconstruct its clamp only for a
 * run whose metadata predates the section block. `null` — never `0` — when
 * neither is knowable: a zero step is a plausible-looking number that stacks
 * every column at offset 0, while `null` is refused by the callers below.
 */
export const stackOffsetStepM = (meta: LayerStackLayerMeta, p: StackStationParams): number | null => {
  const pos = (v: number | null | undefined): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0);
  const recorded = pos(meta.sections?.offsetStepM);
  if (recorded > 0) return recorded;
  const clamped = Math.max(pos(p.offsetBinM), pos(meta.grid?.effectiveResolutionM));
  return clamped > 0 ? clamped : null;
};

const stationMeta = (
  meta: LayerStackLayerMeta,
  p: StackStationParams,
  bins: StationBin[],
  layer: StackLayer | null,
  offsetStepM: number | null,
  echoedCrossfall: CrossfallParams | null,
): StationVolumesLayerMeta => {
  // The section byproduct is the gate for the whole crossfall disclosure, not
  // just for `sections`: every consumer except the header chip reads the
  // station LINES, and without a resolvable offset axis there are none. A
  // `params.crossfall` offered here with no lines behind it would put two
  // columns of em dashes on the table.
  const hasLines = !!meta.sections && offsetStepM !== null;
  const crossfall = hasLines ? stackCrossfallParams(layer?.crossfall, bins, echoedCrossfall) : null;
  return {
    schemaVersion: 1,
    alignment: {
      name: p.alignmentName,
      key: p.alignmentKey,
      layerId: p.alignmentLayerId,
      sourceHash: p.alignmentSourceHash,
      lengthM: p.stationToInternalM ?? 0,
      // A stack carries no station equations and no chainage origin: the
      // worker's echo has neither. Display stations therefore equal internal
      // ones, which is what the bins themselves already say
      // (`staStartDisplayM === staStartInternalM` on every stack bin).
      staStart: 0,
      equations: [],
      minRadiusM: null,
      curvatureWarning: false,
    },
    params: {
      stationIntervalM: p.stationIntervalM,
      swathLeftM: p.swathLeftM,
      swathRightM: p.swathRightM,
      stationFromInternalM: p.stationFromInternalM ?? 0,
      stationToInternalM: p.stationToInternalM ?? 0,
      // The EFFECTIVE step (see `stackOffsetStepM`), never the requested one.
      // `NaN` rather than `0` for the unknowable case: every `offsetStepM > 0`
      // guard in `sectionModel.ts` and in the two decode hooks rejects it, and
      // nothing downstream can mistake it for a measured column width. A zero
      // passes none of those tests and quietly draws a corridor of no width.
      offsetBinM: offsetStepM ?? Number.NaN,
      minCoveragePct: p.minCoveragePct ?? 0,
      effectiveResolutionM: meta.grid?.effectiveResolutionM ?? 0,
      cellAreaM2: (meta.grid?.effectiveResolutionM ?? 0) ** 2,
      method: 'RASTER_INTEGRATION',
      densifyStepM: 1,
      extent: 'CORRIDOR_BBOX',
      // `stack_analysis.py` hands `compute_station_bins` the design raster's
      // footprint as `ref_mask`, so a stack's bins are on the reference basis.
      coverageBasis: 'REFERENCE_SURFACE',
      // Absent, not `null`, on a run with no check — `station_bins.py` omits the
      // key entirely and every gate in the dock asks `!= null`.
      ...(crossfall ? { crossfall } : {}),
    },
    // The roll-up rides on the summary whether or not the bands were
    // recoverable: the chip prints COUNTS, which are real either way.
    summary: stackSummary(bins, layer?.crossfall),
    offsetBins: null,
    // Offered only when the offset axis is known. `sections` is THE predicate
    // `useStationSections` fetches on, so a run whose step cannot be resolved
    // gets no section rather than a degenerate one — the Section tab falls back
    // to saying it has nothing, which is true.
    sections:
      meta.sections && offsetStepM !== null
        ? {
            nStationLines: meta.sections.nStationLines,
            nOffsets: meta.sections.nOffsets,
            planes: meta.sections.planes,
          }
        : null,
    byproducts: {
      // A stack has no `station_volumes.json` and no `station_offsets.f32`. The
      // dock's own `useStationVolumes` / `useStationOffsets` are disabled by
      // these nulls rather than by a second flag, so there is exactly one place
      // that decides whether a fetch happens.
      json: null,
      csv: meta.byproducts?.stationsCsv ?? null,
      offsets: null,
      sections: meta.byproducts?.sections ?? null,
    },
    // The two SURFACES either side of this layer, by name — what the section
    // legend and the CSV header call "top" and "bottom".
    topLayerName: layer?.levelFrom ?? null,
    bottomLayerName: layer?.levelTo ?? null,
  };
};

/** One LEVEL's tolerances, as the section chart takes them. */
export interface StackLevelTolerance {
  underToleranceM: number | null;
  overToleranceM: number | null;
}

/**
 * Every level's tolerances, in level order — what the stacked cross-section
 * classifies each band against.
 *
 * It has to be per LEVEL rather than per run: a stack's layers are judged
 * against different numbers (`stack_analysis.py` judges layer `i` with level
 * `i`'s pair, the TOP of the two), and a stack result layer carries no
 * run-wide expectation block at all — neither `deviationStats.corridor` nor
 * `surfaceDifference.stats` exists on it. Without this the section has
 * nothing to compare a column to and draws clean while the compliance chip
 * beside it reads FAIL.
 *
 * `null` when the run predates the echo (`stack_analysis.py` wrote `levels[]`
 * as name+state only until then). Never a zero stand-in: zero marks every
 * column out of spec.
 */
export const stackLevelTolerances = (meta: LayerStackLayerMeta | null): (StackLevelTolerance | null)[] | null => {
  const levels = meta?.levels;
  if (!Array.isArray(levels) || levels.length === 0) return null;
  const out = levels.map((lv) => {
    const under = typeof lv?.underTolM === 'number' && Number.isFinite(lv.underTolM) ? lv.underTolM : null;
    const over = typeof lv?.overTolM === 'number' && Number.isFinite(lv.overTolM) ? lv.overTolM : null;
    return under === null && over === null ? null : { underToleranceM: under, overToleranceM: over };
  });
  return out.some((t) => t !== null) ? out : null;
};

export interface StackDockData {
  /** The station meta the dock reads, for the SELECTED layer. */
  meta: StationVolumesLayerMeta;
  /** `null` until the stack document is fetched, or for a PENDING layer. */
  json: StationVolumesJson | null;
  /** The selected layer's full record, `null` before the document lands. */
  layer: StackLayer | null;
  /** The section planes bounding the selected layer. */
  planePair: [string, string] | null;
  /**
   * Every LEVEL's tolerances, in level order — not just the selected layer's.
   * The section draws the WHOLE stack at once and marks each band against its
   * own pair, so it needs all of them however the chips are clicked.
   */
  levelTolerances: (StackLevelTolerance | null)[] | null;
}

/**
 * Everything the dock needs for one layer of a stack.
 *
 * `null` when the run carried no station breakdown at all — the dock has
 * nothing to plot, and says so rather than drawing an empty corridor.
 */
export const stackDockData = (
  meta: LayerStackLayerMeta | null,
  doc: StackResult | null,
  layerIndex: number,
): StackDockData | null => {
  if (!meta) return null;
  const p = meta.stationParams;
  if (!p) return null;
  const layer = doc?.layers?.[layerIndex] ?? null;
  // A PENDING layer has no `stations` key at all — the layer exists, nothing
  // was measured. An empty array, not a fabricated bin set.
  const bins = layer?.stations ?? [];
  // Resolved ONCE and threaded everywhere the offset axis appears, so the
  // synthesised meta, the synthesised json and the section byproduct can never
  // disagree about how wide a column is.
  const offsetStepM = stackOffsetStepM(meta, p);
  const sections = offsetStepM === null ? null : meta.sections;
  // The echo is on the DOCUMENT, not the layer: one run, one crossfall spec.
  const stationVolumesMeta = stationMeta(meta, p, bins, layer, offsetStepM, doc?.params?.crossfall ?? null);
  return {
    meta: stationVolumesMeta,
    json: doc
      ? {
          schemaVersion: 1,
          analysisKind: 'layer stack',
          alignment: stationVolumesMeta.alignment,
          params: stationVolumesMeta.params,
          summary: stationVolumesMeta.summary,
          offsetBins: {
            file: '',
            layout: 'planes',
            planes: ['dz', 'zTop', 'zBottom'],
            nStations: bins.length,
            nOffsets: sections?.nOffsets ?? 0,
            // A stack writes no `station_offsets.f32`, so this block is a
            // placeholder — but it still has to describe the SAME axis the
            // section planes are on, or the two halves of one document
            // disagree with each other.
            offsetStartM: sections?.offsetStartM ?? -p.swathLeftM,
            offsetStepM: offsetStepM ?? Number.NaN,
            nodata: 'NaN',
            byteLength: 0,
          },
          ...(sections ? { sections, stations: stackStationLines(meta, p, bins) } : {}),
          bins,
        }
      : null,
    layer,
    planePair: stackPlanePair(meta, layerIndex),
    levelTolerances: stackLevelTolerances(meta),
  };
};
